import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Switch from '@mui/material/Switch';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { toast } from 'react-toastify';
import UrsHeader from '../components/UrsHeader';
import Api from '../Api';
import Account from '../helpers/Account';
import { profileUrsRequest } from '../store/actions/users';

function formatTelegramContact(entry) {
    if (!entry) return '—';
    if (entry.telegram_username) return `@${entry.telegram_username}`;
    if (entry.telegram_phone) return `+${entry.telegram_phone}`;
    return '—';
}

function hasTelegramContact(entry) {
    return Boolean(entry?.telegram_username || entry?.telegram_phone);
}

function UrsAdmGroup() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const profile = useSelector((state) => state.users.ursData);
    const profileLoaded = useSelector((state) => state.users.fetchedAdmin);
    const canManage = profileLoaded && +profile?.admingroup === 1;
    const [loading, setLoading] = useState(true);
    const [admins, setAdmins] = useState([]);
    const [telegramByAdmin, setTelegramByAdmin] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [modalAdmin, setModalAdmin] = useState(null);
    const [contactType, setContactType] = useState('username'); // username | phone
    const [telegramInput, setTelegramInput] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!Account.getUrsToken()) {
            navigate('/admin_login');
        }
    }, [navigate]);

    useEffect(() => {
        if (profileLoaded && profile.admingroup && +profile.admingroup !== 1) {
            navigate('/not-found');
        }
    }, [profile, profileLoaded, navigate]);

    useEffect(() => {
        (async () => {
            await dispatch(profileUrsRequest());
        })();
    }, [dispatch]);

    const loadData = async () => {
        try {
            setLoading(true);
            const { data } = await Api.ursAdmins();
            setAdmins(data?.admins || []);
            setTelegramByAdmin(data?.telegramUsers || {});
        } catch (e) {
            console.log(e);
            setAdmins([]);
            setTelegramByAdmin({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const rows = useMemo(
        () => [...admins].sort((a, b) => new Date(b.sdate) - new Date(a.sdate)),
        [admins]
    );

    const isEnabled = (adminId) => hasTelegramContact(telegramByAdmin[String(adminId)]);

    const handleToggle = async (row, checked) => {
        if (!canManage) return;

        if (checked) {
            const existing = telegramByAdmin[String(row.id)] || {};
            const hasPhone = Boolean(existing.telegram_phone);
            const hasUser = Boolean(existing.telegram_username);
            setModalAdmin(row);
            if (hasPhone && !hasUser) {
                setContactType('phone');
                setTelegramInput(existing.telegram_phone || '');
            } else {
                setContactType('username');
                setTelegramInput(existing.telegram_username ? `@${existing.telegram_username}` : '');
            }
            setModalOpen(true);
            return;
        }

        try {
            const { data } = await Api.removeUrsAdminTelegramUser(row.id);
            setTelegramByAdmin(data?.telegramUsers || {});
            toast.success('Removed');
        } catch (e) {
            console.log(e);
            toast.error('Could not remove');
        }
    };

    const closeModal = () => {
        if (saving) return;
        setModalOpen(false);
        setModalAdmin(null);
        setTelegramInput('');
        setContactType('username');
    };

    const handleSaveTelegram = async () => {
        if (!modalAdmin) return;
        const value = telegramInput.trim();
        if (!value) {
            toast.error(contactType === 'phone' ? 'Enter Telegram phone' : 'Enter Telegram username');
            return;
        }
        try {
            setSaving(true);
            const payload = { adminId: modalAdmin.id };
            if (contactType === 'phone') {
                payload.telegramPhone = value;
            } else {
                payload.telegramUsername = value;
            }
            const { data } = await Api.saveUrsAdminTelegramUser(payload);
            setTelegramByAdmin(data?.telegramUsers || {});
            toast.success('Saved');
            closeModal();
        } catch (e) {
            console.log(e);
            toast.error(e?.response?.data?.error || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (value) => {
        if (!value) return '—';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return String(value);
        return d.toLocaleString();
    };

    return (
        <>
            <Helmet>
                <title>Admin Panel — Telegram helper</title>
            </Helmet>
            <UrsHeader />
            <div className="urs_table" style={{ display: 'flex', justifyContent: 'center', paddingTop: 20 }}>
                <div
                    className="container tableParent file"
                    style={
                        window.innerWidth <= 1024
                            ? { overflow: 'auto', background: 'white', padding: '35px 15px 15px 15px' }
                            : { background: 'white', padding: '35px 15px 35px 15px', maxWidth: 1024 }
                    }
                >
                    <div style={{ marginBottom: 16 }}>
                        <Link to="/urs" className="custom_span" style={{ color: '#5e8516' }}>
                            ← Back to members
                        </Link>
                    </div>
                    <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>Telegram helper</h2>
                    <p style={{ margin: '0 0 16px', color: '#666', fontSize: 14 }}>
                        Enable an admin and set their Telegram username or phone (for accounts without a username).
                    </p>
                    <Paper elevation={0} sx={{ width: '100%', boxShadow: 'none' }}>
                        <TableContainer>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Login</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Phone</TableCell>
                                        <TableCell>Telegram</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell align="center">Enabled</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                Loading...
                                            </TableCell>
                                        </TableRow>
                                    ) : rows.length ? (
                                        rows.map((row) => {
                                            const entry = telegramByAdmin[String(row.id)];
                                            return (
                                                <TableRow key={row.id} hover>
                                                    <TableCell>{row.name || '—'}</TableCell>
                                                    <TableCell>{row.fio || '—'}</TableCell>
                                                    <TableCell>{row.phone || '—'}</TableCell>
                                                    <TableCell>{formatTelegramContact(entry)}</TableCell>
                                                    <TableCell>{formatDate(row.sdate)}</TableCell>
                                                    <TableCell align="center">
                                                        <Switch
                                                            checked={isEnabled(row.id)}
                                                            onChange={(e) => handleToggle(row, e.target.checked)}
                                                            disabled={!canManage}
                                                            color="success"
                                                            inputProps={{ 'aria-label': `toggle admin ${row.id}` }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                No admins found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </div>
            </div>

            <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="xs">
                <DialogTitle>Telegram contact</DialogTitle>
                <DialogContent>
                    <p style={{ margin: '0 0 12px', fontSize: 14, color: '#555' }}>
                        {modalAdmin ? `${modalAdmin.fio || modalAdmin.name || 'Admin'} (id ${modalAdmin.id})` : ''}
                    </p>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={contactType}
                        onChange={(_, next) => {
                            if (!next) return;
                            setContactType(next);
                            setTelegramInput('');
                        }}
                        sx={{ mb: 2 }}
                        disabled={saving}
                    >
                        <ToggleButton value="username">Username</ToggleButton>
                        <ToggleButton value="phone">Phone</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                        autoFocus
                        fullWidth
                        label={contactType === 'phone' ? 'Telegram phone' : 'Telegram username'}
                        placeholder={contactType === 'phone' ? '+374XXXXXXXX' : '@username'}
                        value={telegramInput}
                        onChange={(e) => setTelegramInput(e.target.value)}
                        disabled={saving}
                        helperText={
                            contactType === 'phone'
                                ? 'Use international format without spaces (e.g. +37444123456)'
                                : 'For users who have a public Telegram username'
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeModal} disabled={saving}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={handleSaveTelegram} disabled={saving}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default UrsAdmGroup;
